import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRightCircle, CheckCircle2 } from "lucide-react";
import { api, num } from "../../lib/api";

const Conf = ({ v }) => {
  const pct = Math.round((v || 0) * 100);
  const color = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2 w-20">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-slate-500 font-mono">{pct}%</span>
    </div>
  );
};

const SOURCE = {
  prezzario: { label: "Prezzario", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  stima: { label: "Stima AI", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  documento: { label: "Da documento", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

const PriceSource = ({ it }) => {
  const s = SOURCE[it.price_source];
  if (!s) return <span className="text-[10px] text-red-500 font-semibold">prezzo mancante</span>;
  const title = it.price_source === "prezzario" ? `${it.code} — ${it.prezzario_description || ""}` : it.price_note || "";
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${s.cls}`}>
      {s.label}{it.price_source === "prezzario" && it.code ? <span className="font-mono">{it.code}</span> : null}
      {it.price_source === "prezzario" && it.match_confidence > 0 ? <span className="font-mono opacity-70">{Math.round(it.match_confidence * 100)}%</span> : null}
    </span>
  );
};

export default function AnalysisTab({ offerId, onTransferred }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferringOriginal, setTransferringOriginal] = useState(false);

  useEffect(() => { api.getAnalysis(offerId).then((r) => setResult(r.result)).catch(() => {}); }, [offerId]);

  const analyze = async () => {
    setLoading(true);
    try {
      const started = await api.analyze(offerId);
      const jobId = started?.job_id;
      if (!jobId) throw new Error("Job di analisi non avviato");

      let status = started?.status || "QUEUED";
      for (let i = 0; i < 900 && ["QUEUED", "RUNNING"].includes(status); i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const job = await api.analyzeStatus(offerId, jobId);
        status = job?.status || "FAILED";
        if (status === "FAILED") throw new Error(job?.error || "Analisi non completata");
      }
      if (["QUEUED", "RUNNING"].includes(status)) {
        throw new Error("Analisi ancora in corso oltre il tempo previsto");
      }

      const latest = await api.getAnalysis(offerId);
      const r = latest?.result;
      if (!r) throw new Error("Analisi completata senza risultato disponibile");
      setResult(r);
      const refresh = r?.engine_report?.source_refresh;
      toast.success(refresh
        ? `Rianalisi completata: ${refresh.active_files || 0} file riletti, ${refresh.duplicates_removed || 0} duplicati eliminati`
        : "Analisi completata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore durante l'analisi");
    } finally {
      setLoading(false);
    }
  };

  const transfer = async () => {
    setTransferring(true);
    try {
      const r = await api.transferAnalysis(offerId);
      toast.success(`${r.transferred} voci trasferite nel CME`);
      onTransferred?.();
    } catch {
      toast.error("Errore nel trasferimento");
    } finally {
      setTransferring(false);
    }
  };

  const transferOriginal = async () => {
    setTransferringOriginal(true);
    try {
      const r = await api.transferAnalysisOriginal(offerId);
      toast.success(r.transferred + " voci trasferite con i prezzi originali dell’offerta");
      onTransferred?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel trasferimento prezzi originali"); }
    finally { setTransferringOriginal(false); }
  };

  return (
    <div className="space-y-5 fade-up">
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-semibold text-lg text-slate-900">Analisi tecnica dell’offerta</h3>
          <p className="text-sm text-slate-500">Un’unica analisi coordina BIM/XLSX, elaborati PDF vettoriali, computo, Scope Gap e AI. I motori deterministici verificano prima i dati tecnici; l’AI interviene poi per interpretazione e classificazione.</p>
        </div>
        <button onClick={analyze} disabled={loading} data-testid="ai-doc-analyzer-start-button"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white ${loading ? "bg-orange-400" : "bg-orange-600 hover:bg-orange-700 active:scale-95"} ${loading ? "" : "pulse-ring"}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? (result ? "Ricostruzione completa in corso…" : "Analisi tecnica in corso…") : result ? "Rianalizza offerta" : "Analizza offerta"}
        </button>
      </div>

      {result && (
        <>
          {result.engine_report && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Motori eseguiti</div>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 text-xs">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3"><div className="font-semibold text-emerald-800">Sorgenti</div><div className="text-slate-600 mt-1">{result.engine_report.source_refresh?.active_files ?? result.engine_report.files ?? 0} riletti · {result.engine_report.source_refresh?.duplicates_removed || 0} duplicati rimossi</div></div>
                <div className="rounded-lg bg-sky-50 border border-sky-200 p-3"><div className="font-semibold text-sky-800">BIM</div><div className="text-slate-600 mt-1">{result.engine_report.bim?.analyzed_files || 0} file · {result.engine_report.bim?.elements || 0} elementi</div></div>
                <div className="rounded-lg bg-violet-50 border border-violet-200 p-3"><div className="font-semibold text-violet-800">PDF vettoriali</div><div className="text-slate-600 mt-1">{result.engine_report.pdf_takeoff?.analyzed_files || 0} file · {result.engine_report.pdf_takeoff?.pages || 0} pagine</div></div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="font-semibold text-slate-800">Computo</div><div className="text-slate-600 mt-1">{result.engine_report.cme?.existing_items || 0} voci esistenti</div></div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3"><div className="font-semibold text-amber-800">Verifica progetto</div><div className="text-slate-600 mt-1">{result.engine_report.scope?.gaps || 0} gap · {result.engine_report.scope?.missing_scope || 0} scope mancanti</div></div>
                <div className="rounded-lg bg-orange-50 border border-orange-200 p-3"><div className="font-semibold text-orange-800">AI</div><div className="text-slate-600 mt-1">{result.engine_report.ai?.executed ? "Completata" : result.engine_report.ai?.error === "quota_exhausted" ? "Credito API esaurito" : "Non disponibile"}</div></div>
              </div>
            </div>
          )}

          {result.summary && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-5">
              <div className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">Sintesi AI</div>
              <p className="text-sm text-slate-700">{result.summary}</p>
            </div>
          )}

          {result.price_stats && result.items?.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="analysis-price-stats">
              <span className="px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">{result.price_stats.prezzario} prezzi da prezzario ufficiale</span>
              <span className="px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 font-semibold">{result.price_stats.stima} stimati dall'AI</span>
              {result.price_stats.mancanti > 0 && <span className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 font-semibold">{result.price_stats.mancanti} senza prezzo</span>}
              {!result.price_stats.prezzario_disponibile && <span className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-500 border border-slate-200">Nessun prezzario per questa regione: usate solo stime</span>}
            </div>
          )}

          {result.items?.length > 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h4 className="font-semibold text-slate-900">Lavorazioni rilevate ({result.items.length})</h4>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button onClick={transferOriginal} disabled={transferringOriginal} data-testid="analysis-transfer-original-button"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100 disabled:opacity-60">
                    {transferringOriginal ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightCircle className="w-4 h-4" />}
                    Trasferisci prezzi dell’offerta
                  </button>
                  <button onClick={transfer} disabled={transferring} data-testid="analysis-transfer-button"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 disabled:opacity-60">
                    {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Valida e trasferisci AI
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50">
                      <th className="px-4 py-2.5 font-semibold">WBS</th>
                      <th className="px-4 py-2.5 font-semibold">Categoria</th>
                      <th className="px-4 py-2.5 font-semibold">Descrizione</th>
                      <th className="px-4 py-2.5 font-semibold text-right">U.M.</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Q.tà</th>
                      <th className="px-4 py-2.5 font-semibold text-right">P.U.</th>
                      <th className="px-4 py-2.5 font-semibold">Fonte prezzo</th>
                      <th className="px-4 py-2.5 font-semibold">Confidenza</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.items.map((it, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-mono text-xs text-orange-600">{it.wbs}</td>
                        <td className="px-4 py-2.5 text-slate-600">{it.category}{it.subcategory ? ` · ${it.subcategory}` : ""}</td>
                        <td className="px-4 py-2.5 text-slate-800 max-w-md">{it.description}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{it.unit}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{num(it.quantity)}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{num(it.unit_price)}</td>
                        <td className="px-4 py-2.5" data-testid={`analysis-price-source-${i}`}><PriceSource it={it} /></td>
                        <td className="px-4 py-2.5"><Conf v={it.confidence} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-600">
              <div className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" /><div><div className="font-semibold text-slate-800">Verifiche tecniche completate</div><div className="mt-1">L’AI non ha prodotto nuove voci da trasferire. Il computo RDO importato resta la base economica; usa <b>4. Verifica progetto</b> per il confronto deterministico RDO ↔ BIM ↔ Tavole.</div>{result.ai_partial && <div className="mt-2 text-xs text-amber-700">Fase AI non completata; i risultati BIM, PDF vettoriali e Scope Gap restano disponibili.</div>}</div></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
